/**
 * <ia-search-counts>, the numbers on the search page.
 *
 * Liquid can only answer for the type it was asked about, so the active tab's
 * count ships with the render and every other tab costs one section-rendering
 * call. The heading is their SUM: measured 2026-08-11, a typeless search is NOT
 * the sum (non-product types contribute exactly 2 to it), so it is unusable as
 * a total.
 *
 * A tab that comes back empty hides itself. It cannot hide before its number
 * arrives, so a tab is only ever removed, never flashed into existence.
 */
import { EVENTS, publish, subscribe } from 'ia-util-pubsub';
import { readCounts, writeCounts, fetchCount } from 'ia-search-count-cache';

/**
 * With filters on, `search.results_count` counts the FILTERED set, and the
 * heading and tabs must not follow it (decision: they always answer the query,
 * the narrowed number lives on the Filter & Sort counter). So a filtered render
 * is not a source of counts at all.
 */
function isFiltered() {
  return [...new URLSearchParams(window.location.search).keys()].some((key) => key.startsWith('filter.'));
}


class IaSearchCounts extends HTMLElement {
  async connectedCallback() {
    this.terms = this.dataset.terms || '';
    if (!this.terms) return;

    // A bare /search?q= renders the first tab but keeps an address that says
    // nothing: send the shopper to the canonical one before anything else, or
    // reload and back button disagree with what is on screen.
    if (this.hasAttribute('data-needs-canonical')) {
      const url = new URL(window.location.href);
      url.searchParams.set('type', this.dataset.activeType);
      window.location.replace(url.toString());
      return;
    }

    this.bindField();
    this.bindNavigation();
    this.watchRenders();
    if (!this.tabs.length) return;

    const active = this.dataset.activeType;
    const counts = { ...(readCounts(this.terms) || {}) };
    // The render is fresher than anything stored, unless it was filtered, in
    // which case its number answers a different question and is dropped.
    if (!isFiltered()) counts[active] = Number(this.dataset.activeCount) || 0;

    const missing = this.tabs.map((tab) => tab.dataset.searchTab).filter((type) => counts[type] === undefined);
    this.paint(counts, missing.length > 0);

    if (missing.length) {
      const signal = this.startRound();
      const fetched = await Promise.all(missing.map((type) => this.count(type, signal)));
      if (signal.aborted) return;
      missing.forEach((type, index) => {
        if (fetched[index] !== null) counts[type] = fetched[index];
      });
      this.paint(counts, false);
    }
    writeCounts(this.terms, counts);
    this.leaveEmptyTab(counts, active);
  }

  /**
   * The tabs and the heading number are looked up on every use, never captured
   * at connect: they ride the filter bar, which sits far down the section and
   * is re-rendered on every transition, so a snapshot taken while the page was
   * still parsing held nothing at all (symptom: every tab painted 0).
   */
  get tabs() {
    return [...this.querySelectorAll('[data-search-tab]')];
  }

  get total() {
    return this.querySelector('[data-search-total]');
  }

  /**
   * Said out loud once the numbers have settled: the total, and what each tab
   * holds, because the tabs are how the shopper gets to the other half of the
   * answer and a number they cannot see is not an offer.
   */
  announce(sum, counts) {
    const status = this.querySelector('[data-search-status]');
    if (!status) return;
    const perTab = this.tabs
      .filter((tab) => !tab.hidden)
      .map((tab) => `${tab.dataset.searchTab}: ${counts[tab.dataset.searchTab] ?? 0}`)
      .join(', ');
    status.textContent = perTab ? `${sum} results. ${perTab}` : `${sum} results`;
  }

  /**
   * Standing on a tab that has nothing while the other one does: the shopper
   * asked the site a question and got an empty room, with the answer next door.
   * Sends them there, replacing the entry so Back does not bounce them into the
   * empty tab again.
   */
  leaveEmptyTab(counts, activeType, live) {
    if (counts[activeType] !== 0) return;
    const elsewhere = this.tabs.find((tab) => counts[tab.dataset.searchTab] > 0);
    if (!elsewhere) return;
    // Replace, never push: Back must not drop the shopper into the empty tab
    // they were just rescued from. Mid-visit the engine is already loaded, so
    // the move stays a re-render instead of a reload.
    if (!live) {
      window.location.replace(elsewhere.href);
      return;
    }
    history.replaceState({}, '', elsewhere.href);
    publish(EVENTS.urlChanged, {});
  }

  /** The shared per-type count request (ia-search-count-cache). */
  count(type, signal) {
    const searchUrl = `${window.Shopify?.routes?.root || '/'}search`;
    return fetchCount(this.terms, type, { searchUrl, signal });
  }

  /**
   * `pending` keeps the total honest while numbers are still travelling: a sum
   * of what has arrived so far would tick upwards in front of the shopper.
   */
  paint(counts, pending) {
    this.tabs.forEach((tab) => {
      const type = tab.dataset.searchTab;
      const value = counts[type];
      const slot = tab.querySelector('[data-search-tab-count]');
      if (slot && value !== undefined) slot.textContent = value;
      // Only ever hide, and only once the number is in: an empty tab is a fact,
      // a missing tab is a guess.
      if (value === 0) tab.hidden = true;
    });

    // One tab is no choice: with a single one left standing the row is not a
    // control any more, so it goes. (No mockup exists for a one-tab row.)
    const visible = this.tabs.filter((tab) => !tab.hidden);
    const row = this.querySelector('[data-search-tabs]');
    if (row && !pending) row.hidden = visible.length < 2;

    const total = this.total;
    if (!total) return;
    if (pending) {
      total.dataset.loading = '';
      return;
    }
    const sum = this.tabs
      .map((tab) => counts[tab.dataset.searchTab])
      .filter((value) => value !== undefined)
      .reduce((a, b) => a + b, 0);
    total.textContent = sum;
    delete total.dataset.loading;
    this.announce(sum, counts);
  }

  /**
   * Tabs change the address without a reload: pushState is enough, because
   * ia-filter-core intercepts it, fetches the section and publishes the fresh
   * markup, and a tab switch stays inside the results state.
   *
   * The form is different: a new term may flip the page between results and
   * the empty screen, and the transition engine plus the area it swaps only
   * exist on the results side (a fetched empty state has no products area to
   * put anywhere, and an empty page has no engine to answer at all). So a
   * submit is a real navigation and the server renders whichever state the
   * new term earns.
   */
  bindNavigation() {
    // Each tab remembers the last address it was seen at, filters and sort
    // included, so coming back restores the filtered view (decision). A tab
    // never visited falls back to its plain href; a new query wipes the lot.
    this.lastSeen = {};
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-search-tab]');
      if (!tab || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      if (tab.getAttribute('aria-selected') === 'true') return;
      this.lastSeen[this.dataset.activeType] = window.location.search;
      history.pushState({}, '', this.lastSeen[tab.dataset.searchTab] || tab.href);
    });

    const form = this.querySelector('form[role="search"]');
    form?.addEventListener('submit', (event) => {
      const input = this.querySelector('[data-search-input]');
      const terms = input?.value.trim();
      if (!terms) return;
      event.preventDefault();
      const url = new URL(form.action, window.location.origin);
      url.searchParams.set('q', terms);
      url.searchParams.set('type', this.dataset.activeType);
      window.location.assign(url.toString());
    });
  }

  /**
   * The engine answers with the whole section; the grid is its business, the
   * heading and the tabs are ours. Only the values change here: the nodes stay,
   * so focus and the caret survive a tab switch.
   */
  disconnectedCallback() {
    // The theme editor replaces this element on a section re-render; without
    // these two, the detached copy kept answering filters:update and its
    // in-flight count round kept counting for nobody.
    this.unsubscribeFilters?.();
    this.round?.abort();
  }

  watchRenders() {
    this.unsubscribeFilters = subscribe(EVENTS.filtersUpdate, ({ detail }) => {
      if (!detail?.html || detail.append) return;
      const fresh = new DOMParser().parseFromString(detail.html, 'text/html');
      const freshRoot = fresh.querySelector('ia-search-counts');
      if (!freshRoot) return;

      const terms = freshRoot.dataset.terms || '';
      const activeType = freshRoot.dataset.activeType;
      const activeCount = Number(freshRoot.dataset.activeCount) || 0;
      this.dataset.activeType = activeType;
      this.dataset.activeCount = activeCount;

      // A new term is a new question: the cached counts belong to the old one,
      // and so do the remembered per-tab addresses with their filters.
      const sameTerm = terms === this.terms;
      if (!sameTerm) this.lastSeen = {};
      this.terms = terms;

      const heading = fresh.querySelector('[data-search-heading]');
      const mine = this.querySelector('[data-search-heading]');
      if (heading && mine) this.syncHeadingText(mine, heading);

      this.tabs.forEach((tab) => {
        const type = tab.dataset.searchTab;
        const isActive = type === activeType;
        tab.setAttribute('aria-selected', isActive);
        tab.classList.toggle('is-active', isActive);
        tab.href = tab.href.replace(/([?&]q=)[^&]*/, `$1${encodeURIComponent(terms)}`);
      });

      this.refresh(sameTerm ? readCounts(terms) : null, activeType, isFiltered() ? undefined : activeCount);
      // Not on a re-render: switching tabs by hand is a choice, and a filter
      // that empties the grid is the shopper's own doing. Only the arrival on
      // a dead tab is worth correcting.
    });
  }

  /**
   * The heading is one line of text around two moving parts; replacing it
   * wholesale would take the preloader with it, so only the term is copied and
   * the number is left to paint().
   */
  syncHeadingText(mine, fresh) {
    const term = (node) => node.querySelector('[data-search-terms]');
    const from = term(fresh);
    const to = term(mine);
    if (from && to) to.textContent = from.textContent;
  }

  /** Repaints from whatever is known, fetching only what is genuinely missing. */
  /**
   * One round of counting at a time. Typing a new term while the old one is
   * still travelling used to let the late answer paint over the new question;
   * every round now cancels the one before it.
   */
  startRound() {
    this.round?.abort();
    this.round = new AbortController();
    return this.round.signal;
  }

  async refresh(cached, activeType, activeCount) {
    const counts = { ...(cached || {}) };
    if (activeCount !== undefined) counts[activeType] = activeCount;
    const missing = this.tabs.map((t) => t.dataset.searchTab).filter((type) => counts[type] === undefined);
    this.tabs.forEach((tab) => { tab.hidden = false; });
    this.paint(counts, missing.length > 0);
    if (missing.length) {
      const signal = this.startRound();
      const fetched = await Promise.all(missing.map((type) => this.count(type, signal)));
      if (signal.aborted) return;
      missing.forEach((type, index) => {
        if (fetched[index] !== null) counts[type] = fetched[index];
      });
      this.paint(counts, false);
    }
    writeCounts(this.terms, counts);
    // Same rescue as on arrival: a submit can land on a tab the new query left
    // empty, and standing there with the answer next door is the worst of both.
    this.leaveEmptyTab(counts, activeType, true);
  }

  /**
   * Clear resets the whole search by navigating to the pristine page, address
   * included: a cleared field over yesterday's results reads as results for
   * nothing. There is always a search to reset here, because this binding only
   * runs on a performed one (the early return in connectedCallback).
   */
  bindField() {
    const input = this.querySelector('[data-search-input]');
    const clear = this.querySelector('[data-search-clear]');
    if (!input || !clear) return;
    clear.addEventListener('click', () => {
      window.location.assign(new URL(input.form.action, window.location.origin).toString());
    });
    input.addEventListener('input', () => {
      clear.classList.toggle('ia-hidden', input.value.trim() === '');
    });
  }
}

if (!customElements.get('ia-search-counts')) {
  customElements.define('ia-search-counts', IaSearchCounts);
}
